import { format, isValid, parse } from "date-fns";
import moment from "moment";

/** Display modes for the reusable TimePicker. */
export const TIME_FORMAT_12H = "12h";
export const TIME_FORMAT_24H = "24h";

/**
 * Canonical API / form-storage time format.
 * UI may show 12h or 24h; values emitted to forms/APIs must use this.
 */
export const API_TIME_FORMAT = "HH:mm";

/** Fallback until doctor time format is provided after login/API. */
export const DEFAULT_DOCTOR_TIME_FORMAT = TIME_FORMAT_24H;

/**
 * Normalize common doctor/UI time-format values to `12h` or `24h`.
 * Accepts 12 / 24, labels like "12 Hours", and pattern-like values.
 */
export const normalizeTimeFormat = (timeFormat) => {
  if (timeFormat == null || timeFormat === "") return DEFAULT_DOCTOR_TIME_FORMAT;

  // Numeric configs from future login/API: 12 | 24
  if (timeFormat === 12 || timeFormat === "12") return TIME_FORMAT_12H;
  if (timeFormat === 24 || timeFormat === "24") return TIME_FORMAT_24H;

  const raw = String(timeFormat).trim().toLowerCase().replace(/[_-]+/g, " ");

  if (
    raw === "12h" ||
    raw === "12hr" ||
    raw === "12 hrs" ||
    raw === "12 hour" ||
    raw === "12 hours" ||
    raw === "hh:mm a" ||
    raw === "hh:mm aa" ||
    raw === "h:mm a" ||
    raw === "h:mm aa" ||
    raw === "ampm" ||
    raw === "am/pm" ||
    raw.includes("12 hour")
  ) {
    return TIME_FORMAT_12H;
  }

  if (
    raw === "24h" ||
    raw === "24hr" ||
    raw === "24 hrs" ||
    raw === "24 hour" ||
    raw === "24 hours" ||
    raw === "hh:mm" ||
    raw.includes("24 hour")
  ) {
    return TIME_FORMAT_24H;
  }

  return TIME_FORMAT_24H;
};

/**
 * Resolve the doctor's preferred display time format.
 * Reads common login/localStorage field names (e.g. doctor.time_format).
 */
export const resolveDoctorTimeFormat = (doctor) =>
  normalizeTimeFormat(
    doctor?.time_format ||
      doctor?.timeFormat ||
      doctor?.preferred_time_format ||
      DEFAULT_DOCTOR_TIME_FORMAT
  );

/**
 * TimePicker display format priority:
 * Explicit prop → doctor stored format → existing default.
 * Empty/whitespace props are treated as missing.
 */
export const resolvePickerTimeFormat = (propFormat, doctor) => {
  const hasExplicit =
    propFormat != null && String(propFormat).trim() !== "";
  if (hasExplicit) return normalizeTimeFormat(propFormat);
  return resolveDoctorTimeFormat(doctor);
};

export const is12HourTimeFormat = (timeFormat) =>
  normalizeTimeFormat(timeFormat) === TIME_FORMAT_12H;

/** date-fns pattern used for display helpers. */
export const getTimeDisplayPattern = (timeFormat) =>
  is12HourTimeFormat(timeFormat) ? "hh:mm aa" : "HH:mm";

/** moment/react-datetime timeFormat string for the existing picker UI. */
export const getMomentTimeFormat = (timeFormat) =>
  is12HourTimeFormat(timeFormat) ? "hh:mm A" : "HH:mm";

/** moment display pattern including seconds (live clocks). */
export const getMomentTimeFormatWithSeconds = (timeFormat) =>
  is12HourTimeFormat(timeFormat) ? "hh:mm:ss A" : "HH:mm:ss";

/** Input placeholder derived from the active time format. */
export const getTimeFormatPlaceholder = (timeFormat) =>
  is12HourTimeFormat(timeFormat) ? "hh:mm AM" : "HH:mm";

export const getTimeFormatMaxLength = (timeFormat) =>
  is12HourTimeFormat(timeFormat) ? 8 : 5;

/** Build a Date on today's calendar date from hours/minutes. */
export const buildTimeDate = (hours, minutes) => {
  const h = Number(hours);
  const m = Number(minutes);
  if (
    !Number.isInteger(h) ||
    !Number.isInteger(m) ||
    h < 0 ||
    h > 23 ||
    m < 0 ||
    m > 59
  ) {
    return null;
  }
  const date = new Date();
  date.setHours(h, m, 0, 0);
  return date;
};

/** Parse API/form value `HH:mm` into a Date. */
export const parseApiTimeValue = (value) => {
  if (value instanceof Date) return isValid(value) ? value : null;
  if (value == null || value === "") return null;

  const text = String(value).trim();
  const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(text);
  if (!match) return null;
  return buildTimeDate(match[1], match[2]);
};

/** Format a Date as API/form value `HH:mm`. */
export const formatApiTimeValue = (date) => {
  if (!(date instanceof Date) || !isValid(date)) return "";
  return format(date, API_TIME_FORMAT);
};

/**
 * Coerce Date / Moment / API `HH:mm` into a valid Moment for DISPLAY.
 * Returns null when invalid. Display-string parsing belongs in formatDisplayTime.
 */
export const toDisplayTimeMoment = (value) => {
  if (value == null || value === "") return null;

  if (moment.isMoment(value)) {
    return value.isValid() ? value.clone() : null;
  }

  if (value instanceof Date) {
    const m = moment(value);
    return m.isValid() ? m : null;
  }

  const text = String(value).trim();
  if (!text) return null;

  const apiMatch = /^([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/.exec(text);
  if (!apiMatch) return null;

  const m = moment(
    `${String(apiMatch[1]).padStart(2, "0")}:${apiMatch[2]}`,
    API_TIME_FORMAT,
    true
  );
  return m.isValid() ? m : null;
};

/**
 * Shared DISPLAY time helper (Moment.js).
 * Accepts Date | Moment | API `HH:mm` | 12h display string (when mode is 12h).
 * Returns "" for missing/invalid values. Never used for API payloads.
 * Set includeSeconds for live clocks (HH:mm:ss / hh:mm:ss A).
 */
export const formatDisplayTime = (
  value,
  timeFormat = DEFAULT_DOCTOR_TIME_FORMAT,
  { includeSeconds = false } = {}
) => {
  const resolved =
    timeFormat == null || String(timeFormat).trim() === ""
      ? DEFAULT_DOCTOR_TIME_FORMAT
      : timeFormat;

  let m = toDisplayTimeMoment(value);
  if (!m && value != null && value !== "" && !(value instanceof Date) && !moment.isMoment(value)) {
    const fromTyped = parseTypedTime(String(value).trim(), resolved);
    m = fromTyped ? moment(fromTyped) : null;
  }

  if (!m || !m.isValid()) return "";
  return m.format(
    includeSeconds
      ? getMomentTimeFormatWithSeconds(resolved)
      : getMomentTimeFormat(resolved)
  );
};

/**
 * DISPLAY time using doctor object, with optional explicit format override.
 * Does not read localStorage — pass doctor from context / readStoredDoctorUser.
 * Optional 4th arg: `{ includeSeconds }` for live clocks.
 */
export const formatDoctorDisplayTime = (
  value,
  doctor,
  explicitFormat,
  { includeSeconds = false } = {}
) => {
  const hasExplicit =
    explicitFormat != null && String(explicitFormat).trim() !== "";
  const timeFormat = hasExplicit
    ? normalizeTimeFormat(explicitFormat)
    : resolveDoctorTimeFormat(doctor);
  return formatDisplayTime(value, timeFormat, { includeSeconds });
};

const clampHour24 = (hourText) => {
  if (hourText.length < 2) return hourText;
  const hour = Number(hourText);
  if (Number.isNaN(hour)) return hourText.slice(0, 2);
  return String(Math.min(Math.max(hour, 0), 23)).padStart(2, "0");
};

const clampHour12 = (hourText) => {
  if (hourText.length < 2) return hourText;
  const hour = Number(hourText);
  if (Number.isNaN(hour)) return hourText.slice(0, 2);
  if (hour === 0) return "12";
  return String(Math.min(Math.max(hour, 1), 12)).padStart(2, "0");
};

const clampMinute = (minuteText) => {
  if (minuteText.length < 2) return minuteText;
  const minute = Number(minuteText);
  if (Number.isNaN(minute)) return minuteText.slice(0, 2);
  return String(Math.min(Math.max(minute, 0), 59)).padStart(2, "0");
};

const extractMeridiemDraft = (raw) => {
  const letters = String(raw || "")
    .toUpperCase()
    .replace(/[^APM]/g, "");
  if (!letters) return "";
  if (letters.startsWith("PM") || letters === "P") {
    return letters.startsWith("PM") ? "PM" : "P";
  }
  if (letters.startsWith("AM") || letters === "A") {
    return letters.startsWith("AM") ? "AM" : "A";
  }
  return "";
};

/**
 * Guide typed/pasted input into the configured time format.
 * Supports Backspace/Delete without fighting the mask.
 */
export const maskTimeInput = (
  rawInput,
  timeFormat = DEFAULT_DOCTOR_TIME_FORMAT,
  previousMasked = ""
) => {
  const mode = normalizeTimeFormat(timeFormat);
  const previous = String(previousMasked || "");
  const incoming = String(rawInput || "");
  const isDeleting = incoming.length < previous.length;

  if (mode === TIME_FORMAT_24H) {
    const digits = incoming.replace(/\D/g, "").slice(0, 4);
    const hours = clampHour24(digits.slice(0, 2));
    const minutes = clampMinute(digits.slice(2, 4));

    if (minutes) return `${hours}:${minutes}`;
    if (hours.length === 2 && !isDeleting) return `${hours}:`;
    return hours;
  }

  const digits = incoming.replace(/\D/g, "").slice(0, 4);
  const hours = clampHour12(digits.slice(0, 2));
  const minutes = clampMinute(digits.slice(2, 4));
  const meridiem = extractMeridiemDraft(incoming);

  let result = hours;
  if (minutes) {
    result = `${hours}:${minutes}`;
  } else if (hours.length === 2 && !isDeleting) {
    result = `${hours}:`;
  }

  if (minutes.length === 2) {
    if (meridiem) {
      result = `${hours}:${minutes} ${meridiem}`;
    } else if (!isDeleting) {
      result = `${hours}:${minutes} `;
    } else {
      result = `${hours}:${minutes}`;
    }
  }

  return result;
};

/**
 * Parse typed display text into a Date.
 * Returns null for incomplete/invalid values.
 */
export const parseTypedTime = (text, timeFormat = DEFAULT_DOCTOR_TIME_FORMAT) => {
  if (text == null) return null;
  const raw = String(text).trim();
  if (!raw) return null;

  const mode = normalizeTimeFormat(timeFormat);

  if (mode === TIME_FORMAT_24H) {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(raw);
    if (!match) return null;
    return buildTimeDate(match[1], match[2]);
  }

  const match = /^(0?[1-9]|1[0-2]):([0-5]\d)\s*(AM|PM)$/i.exec(raw);
  if (!match) return null;

  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const meridiem = match[3].toUpperCase();

  if (meridiem === "AM") {
    if (hours === 12) hours = 0;
  } else if (hours !== 12) {
    hours += 12;
  }

  return buildTimeDate(hours, minutes);
};

/**
 * Convert a Date / API string / display string to canonical API `HH:mm`.
 */
export const toApiTimeString = (value, timeFormat = DEFAULT_DOCTOR_TIME_FORMAT) => {
  if (value instanceof Date) return formatApiTimeValue(value);
  if (value == null || value === "") return "";

  const asDate =
    parseApiTimeValue(value) || parseTypedTime(String(value), timeFormat);
  return asDate ? formatApiTimeValue(asDate) : "";
};

/** Parse controlled picker value preferring API `HH:mm`. */
export const parseTimeValue = (value, timeFormat = DEFAULT_DOCTOR_TIME_FORMAT) =>
  parseApiTimeValue(value) || parseTypedTime(value, timeFormat);

/** Convenience: parse with date-fns display pattern (used by picker internals). */
export const parseDisplayTime = (text, timeFormat = DEFAULT_DOCTOR_TIME_FORMAT) => {
  const pattern = getTimeDisplayPattern(timeFormat);
  const parsed = parse(String(text || "").trim(), pattern, new Date());
  return isValid(parsed) ? parsed : parseTypedTime(text, timeFormat);
};
